import _ from "lodash";
import React from "react";
import styled, { css } from "styled-components";
import { DesktopMenuContext } from "../../../contexts/VerticalDesktopMenu";
import { Types } from "../../../types";
import { Helpers } from "../../../utils";
import Icon from "../../Icon";

interface MegamenuRight {
	items: any,
}

const MegamenuRight = ({ items = [] }: MegamenuRight) => {

	const { config, theme, active } = React.useContext(DesktopMenuContext);

	return (
		<MegamenuContainer>
			<SubmenusWrapper {...{ theme, config }}>
				{items.map((item: Types.MenuItem, index: number) => (
					<Submenu key={item.id} {...{ theme, config, item }} />
				))}
			</SubmenusWrapper>
		</MegamenuContainer>
	);
}

export default MegamenuRight;

const Submenu = ({ theme, config, item }: any) => {

	const [selectedItem, setSelectedItem] = React.useState(item);

	const handleSelect = (selectedItem: any) => {
		setSelectedItem(selectedItem ?? item)
	}

	return (
		<StyledSubmenu {...{ theme, config }} onMouseLeave={() => handleSelect(item)}>
			<SubmenuHeader>
				<SubmenuTitle href={item.permalink}>{item.label}</SubmenuTitle>
			</SubmenuHeader>
			<SubmenuContent>
				<SubmenuItems {...{ theme, config, items: item.items, parent: item, onSelect: handleSelect }} />
			</SubmenuContent>
		</StyledSubmenu>
	)

}

const SubmenuItems = ({ theme, config, items, parent, onSelect }: any) => {

	const maxRowsPerSection = 5;

	if (_.isEmpty(items)) {
		return <SubmenuItem {...{ theme, config, item: { ...parent, label: 'Zobraziť produkty' } }} />
	}

	return (
		<>
			{items.slice(0, maxRowsPerSection)?.map((item: Types.MenuItem) => (
				<SubmenuItem key={item.id} {...{ theme, config, item, onSelect }} />
			))}
			{items.length > maxRowsPerSection &&
				<SubmenuShowMore {...{ theme, config, item: { ...parent, label: 'Ďalšie kategórie' } }} />
			}
		</>
	)

}

const SubmenuShowMore = ({ theme, config, item }: any) => {

	return (
		<StyledShowMoreButton>
			<StyledSubmenuItemLink href={item.permalink}>
				<span>+</span>
				<SubmenuItemLabel>
					{item.label}
				</SubmenuItemLabel>
			</StyledSubmenuItemLink>
		</StyledShowMoreButton>
	)

}

const SubmenuItem = ({ theme, config, item, onSelect: handleSelect }: any) => {

	return (
		<StyledSubmenuItem onMouseOver={() => handleSelect && handleSelect(item)} >
			<StyledSubmenuItemLink href={item.permalink}>
				<Icon id={'angleRight'} type={'outlined'} size={1.25} {...{ config }} />
				<SubmenuItemLabel>
					{item.label}
				</SubmenuItemLabel>

			</StyledSubmenuItemLink>
		</StyledSubmenuItem>
	)

}

const MegamenuContainer = styled.div<any>(({ config, theme }) => css`
    display: flex;
    flex: 1;
    width: fit-content;
    box-shadow: 0 0 1em rgba(0,0,0,.1);
    background: linear-gradient(103.85deg, #F8F8F8 5.8%, #FFFFFF 92.33%);
	overflow: hidden;
	font-weight: 600; // navigationBar.textWeight
    // position: relative;
    // top: -0.625em;
`)

const StyledSubmenu = styled.div<any>`
	display: flex;
	flex-direction: column;
    color: currentColor;
	//min-width: clamp(10em, 10vw, 15em);
	//flex: 0 0 calc(33.3333% - 2em);
`

const SubmenusWrapper = styled.div<any>`
	display: flex;
	flex-wrap: wrap;
	width: fit-content;
    max-height: 70vh;
    overflow-y: auto;
	flex: 1;
	height: fit-content;
    color: #2B2D59; // navigationBar.textColor
	gap: 3em 2em;
    margin: 0;
	padding: 2.5em;
    &::-webkit-scrollbar {
        width: ${({ theme: { dark } }) => dark.general.scrollBarSize * 0.25}em;
        background: ${({ theme: { dark } }) => dark.general.scrollBarColor};
    }
    &::-webkit-scrollbar-thumb {
        background: ${({ theme: { dark } }) => dark.general.scrollBarThumbColor};
    }
    @media (max-width: 1150px) {
        ${StyledSubmenu} {
            flex: 0 0 calc(50% - 2em);
        }
    }
`

const SubmenuImageLink = styled.a`
    text-decoration: none;
    background: #fff;
    padding: 0.25em;
    width: 2.25em;
    height: 2.25em;
    border-radius: 0.5em;
    object-fit: contain;
    object-position: center;
    box-shadow: 0.25em 0.5em 1em #2b2d5910;
`

const SubmenuImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
`

const SubmenuTitle = styled.a`
	all: unset;
	cursor: pointer;
	${Helpers.clamp('font-size', 0.9, 1, 1.15)};
    line-height: 1.15;
	color: currentColor;
    opacity: 0.9;
    font-weight: 500;
		margin-bottom: 0.5em;
    &:hover {
        color: currentColor;
        opacity: 1;
    }
`

const SubmenuHeader = styled.div`
	display: flex;
    align-items: center;
    margin-bottom: 0.5em;
    ${SubmenuImageLink} {
        margin-right: ${1.25}em;
    }
`

const SubmenuContent = styled.ul`
	display: flex;
	flex-direction: column;
	gap: .25em;
	padding-left: 0;
	margin: 0;
`

const StyledSubmenuItem = styled.li`
	display: flex !important;
	list-style: none;
    width: fit-content;
    line-height: 1.15; 
    font-weight: 400;
`

const StyledSubmenuItemLink = styled.a<any>(({ config, theme }) => css`
	text-decoration: none;
    all: unset;
	cursor: pointer;
	margin-left: -0.325em;
	display: inline-flex;
	align-items: center;
	font-size: 0.85em;
	text-decoration: none;
	color: currentColor;
	opacity: 0.75;
    font-weight: 400;
    line-height: 1.15; 
    transition: all 300ms ease-in-out;

	&:hover {
        //color: currentColor;
		opacity: 1;
	}
`)

const SubmenuItemLabel = styled.span`
    margin: 0;
    line-height: 1.15; 
`

const StyledShowMoreButton = styled(StyledSubmenuItem)`
    ${StyledSubmenuItemLink} {
        display: flex !important;
        align-items: center;
        border: 0.15em #2b2d597a solid;
        border-radius: 0.325em;
        width: fit-content;
        margin-top: 0.75em;
        margin-left: 0.5em;
        transition: all 300ms ease-in-out;
        padding: 0.325em 0.5em;
        &:hover {
            background-color: #2b2d59 !important;
            color: #fff !important;
        }
    }
    span:not(${SubmenuItemLabel}) {
        margin-right: 0.5em;
    }
`